import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';
import { TDatabaseId } from '@infotech-soe/metaschema';
import { immerable } from 'immer';
import { Set as ImmutableSet } from 'immutable';

import {
  DisableNodeStrategy,
  ICategoryTreeNode,
} from '../../../models/category/categoryTree';

export interface IExtendedTreeNodeInstance extends ITreeNodeInstance {
  name?: string;
  withCheckbox?: boolean;
}

export class CategoryTreeNode implements ICategoryTreeNode {
  public [immerable] = true;

  public id: TDatabaseId;
  public parentId: TDatabaseId | null = null;
  public children: TDatabaseId[] = [];
  public hasAccess = true;
  public hasChildren = false;
  public level = 0;
  public name: string;
  public label: string;
  public first = false;
  public last = false;
  public nodePath: ImmutableSet<TDatabaseId>;
  public skipLevels: number[] = [];
  public disableStrategy: DisableNodeStrategy = DisableNodeStrategy.SINGLE;
  public withCheckbox?: boolean;

  public get disabled(): boolean {
    return !this.hasAccess;
  }

  public get root(): boolean {
    return this.parentId === null;
  }

  public get firstInRoot(): boolean {
    return this.first && this.root;
  }

  constructor(id: TDatabaseId, name: string, label?: string) {
    this.id = id;
    this.name = name;
    this.label = label ?? name;
    this.nodePath = ImmutableSet([id]);
  }

  public addChildNode(node: CategoryTreeNode): void {
    this.addHiddenChildNode(node);
    this.children.push(node.id);

    if (
      this.disableStrategy === DisableNodeStrategy.RECURSIVE &&
      !this.hasAccess
    ) {
      node.disableStrategy = DisableNodeStrategy.RECURSIVE;
      node.hasAccess = false;
    }
  }

  public addHiddenChildNode(node: CategoryTreeNode): void {
    this.hasChildren = true;
    node.inheritProperties(this);
  }

  public inheritProperties(parentNode: CategoryTreeNode): void {
    this.parentId = parentNode.id;
    this.level = parentNode.level + 1;
    this.nodePath = parentNode.nodePath.add(this.id);
    this.skipLevels = [...parentNode.skipLevels];

    if (parentNode.last) {
      this.skipLevels.push(parentNode.level);
    }
  }

  // Actually, model fields `hasAccess` and `hasChildren` have type '0 | 1'
  public static fromServerModel(
    model: IExtendedTreeNodeInstance
  ): CategoryTreeNode {
    /*
    TODO: Actually, label and name fields contains opposite values
     (i.e. "label" contains "name" & vice versa). They must be switched!
    */
    const node = new CategoryTreeNode(model.id, model.label, model.name);

    node.parentId = model.pid;
    node.hasAccess = Boolean(model.hasAccess);
    node.hasChildren = Boolean(model.hasChildren);
    if (model.withCheckbox !== undefined) {
      node.withCheckbox = Boolean(model.withCheckbox);
    }

    if (model.level !== undefined) {
      node.level = model.level;
    }

    return node;
  }

  public toServerModel(): ITreeNodeInstance & { name: string } {
    return {
      pid: this.parentId,
      hasChildren: this.hasChildren,
      id: this.id,
      level: this.level,
      hasAccess: this.hasAccess,
      label: this.name,
      name: this.label,
    };
  }
}
